import {
  Button,
  Text,
  VStack,
  Box,
  Tag,
  useDisclosure,
  ButtonGroup,
  IconButton,
  Icon,
  HStack,
  Spacer,
  Spinner,
  NativeSelect,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import { AddForecastChangeModal } from "./add-forecast-change-modal/AddForecastChangeModal";
import { CreateOrUpdateForecastScenarioModal } from "./CreateOrUpdateForecastScenarioModal";
import { ForecastChangeTooltip } from "./ForecastChangeTooltip";
import { AttributeDefinitions, AttributeName } from "@keita/lanepatrol-sdk";
import {
  ForecastActionType,
  ForecastChange,
} from "../../../../project/models/ForecastScenario";
import { parseAttributeInputValue } from "../../../../coding/utils/attribute-value";
import { AttributeUtils } from "../../../../coding/utils/attribute-utils";
import { getTreatmentLabel } from "../../../../project/utils/treatment-labels";
import { useStores } from "../../../../shared/stores/RootStore";
import { toast } from "../../../../shared/views/CustomToaster";
import { AddIcon, DeleteIcon, SaveIcon } from "../../../../shared/views/icons";
import { CustomTooltip } from "../../../../shared/views/CustomTooltip";

const i18prefix = "map:forecast.display";

const ForecastControlsBase: React.FC = () => {
  const { forecastStore, projectStore } = useStores();

  const addForecastChangeModal = useDisclosure();
  const createOrUpdateModal = useDisclosure();
  const selectedProject = projectStore.getSelected();

  const onRemoveChange = (changeIndex: number) => {
    forecastStore.removeChangeByIndex(changeIndex);
  };

  const onDelete = () => {
    if (
      window.confirm(
        t(`${i18prefix}.delete.confirm`, {
          name: forecastStore.selectedScenario!.name,
        }),
      )
    ) {
      forecastStore
        .deleteForecastScenario(forecastStore.selectedScenario!.id)
        .then(() => toast("success", t(`${i18prefix}.delete.success`)))
        .catch(() => toast("error", t(`${i18prefix}.delete.error`)));
    }
  };

  return (
    <>
      <VStack align="stretch">
        <HStack>
          <Text>{t(`${i18prefix}.title`)}</Text>
          <Spacer />
          {forecastStore.computingValues && (
            <HStack>
              <Spinner color="primary.500" size="xs" />
              <Text fontSize="xs">{t(`${i18prefix}.loading`)}</Text>
            </HStack>
          )}
        </HStack>

        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            fontSize={"sm"}
            value={forecastStore.selectedScenario?.id ?? ""}
            onChange={(e) => forecastStore.setSelectedScenario(e.target.value)}
            placeholder={
              forecastStore.forecastScenarios.length
                ? t(`${i18prefix}.select`)
                : t(`${i18prefix}.selectEmpty`)
            }
          >
            {forecastStore.forecastScenarios.map((scenario) => (
              <option value={scenario.id} key={scenario.id}>
                {scenario.name}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>

        {selectedProject?.permissions.canManageResults && (
          <>
            <ButtonGroup
              size={"sm"}
              colorPalette="primary"
              variant="outline"
              fontSize="sm"
            >
              <Button
                flexGrow={1}
                onClick={createOrUpdateModal.onOpen}
                disabled={!forecastStore.forecastChanges.length}
              >
                <Icon as={SaveIcon} /> {t(`${i18prefix}.saveScenario`)}
              </Button>
              <IconButton
                disabled={!forecastStore.selectedScenario}
                onClick={onDelete}
              >
                <DeleteIcon />
              </IconButton>
            </ButtonGroup>

            <Button
              variant="outline"
              fontSize={"sm"}
              size="sm"
              colorPalette="primary"
              onClick={addForecastChangeModal.onOpen}
            >
              <AddIcon /> {t(`${i18prefix}.addAction`)}
            </Button>
          </>
        )}

        <Box m={-1}>
          {forecastStore.forecastChanges.map((change, i) => (
            <CustomTooltip
              key={i}
              content={<ForecastChangeTooltip change={change} />}
            >
              <Tag.Root m={1} py={1} fontSize="xs">
                <Tag.Label>{getChangeLabel(change)}</Tag.Label>

                {selectedProject?.permissions.canManageResults && (
                  <Tag.EndElement>
                    <Tag.CloseTrigger onClick={() => onRemoveChange(i)} />
                  </Tag.EndElement>
                )}
              </Tag.Root>
            </CustomTooltip>
          ))}
        </Box>
      </VStack>

      {createOrUpdateModal.open && (
        <CreateOrUpdateForecastScenarioModal
          onClose={createOrUpdateModal.onClose}
        />
      )}

      {addForecastChangeModal.open && (
        <AddForecastChangeModal onClose={addForecastChangeModal.onClose} />
      )}
    </>
  );
};

export const ForecastControls = observer(ForecastControlsBase);

const getChangeLabel = (change: ForecastChange): string => {
  switch (change.action.type) {
    case ForecastActionType.AttributeValues: {
      const attributeLabels = (
        Object.entries(change.action.attributeValues) as [
          AttributeName,
          string,
        ][]
      )
        .map(([name, stringValue]) => {
          const attribute = AttributeDefinitions.getByName(name)!;
          const value = parseAttributeInputValue(attribute, stringValue);
          return `${AttributeUtils.getLabel(
            attribute.name,
          )} = ${AttributeUtils.getValueLabel(attribute, value)}`;
        })
        .join(", ");
      return t(`map:forecast.${change.action.type}.tag`, {
        attributes: attributeLabels,
        count: change.results?.size ?? 0,
      });
    }
    case ForecastActionType.TreatmentWhenSuggested: {
      return t(`map:forecast.${change.action.type}.tag`, {
        treatment: getTreatmentLabel(change.action.treatmentType),
        count: change.results?.size ?? 0,
      });
    }
    case ForecastActionType.HighestPriorityTreatment: {
      return t(`map:forecast.${change.action.type}.tag`, {
        count: change.results?.size ?? 0,
      });
    }
    default: {
      return "Not implemented yet";
    }
  }
};
